import React, { FunctionComponent, useMemo } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { EligibilityType } from 'api/gen'
import { useAuthContext } from 'features/auth/context/AuthContext'
import {
  isCurrentOrFormerBeneficiary,
  isCurrentBeneficiary,
} from 'features/auth/helpers/checkStatusType'
import { UserCreditType } from 'features/auth/helpers/getCreditType'
import { LocationWidget } from 'features/location/components/LocationWidget'
import { LocationWidgetDesktop } from 'features/location/components/LocationWidgetDesktop'
import { ScreenOrigin } from 'features/location/enums'
import { usePacificFrancToEuroRate } from 'queries/settings/useSettings'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { formatCurrencyFromCents } from 'shared/currency/formatCurrencyFromCents'
import { useGetCurrencyToDisplay } from 'shared/currency/useGetCurrencyToDisplay'
import { useAvailableCredit } from 'shared/user/useAvailableCredit'
import { PageHeader } from 'ui/components/headers/PageHeader'
import { Separator } from 'ui/components/Separator'
import { Spacer, Typo } from 'ui/theme'

export const HomeHeader: FunctionComponent = function () {
  const availableCredit = useAvailableCredit()
  const { isLoggedIn, user } = useAuthContext()
  const { isDesktopViewport, designSystem } = useTheme()
  const currency = useGetCurrencyToDisplay()
  const { data: euroToPacificFrancRate } = usePacificFrancToEuroRate()
  const numberOfLines = useMobileFontScaleToDisplay({ default: 2, at200PercentZoom: 3 })
  const height = designSystem.size.spacing.xl

  const Header = useMemo(() => {
    const welcomeTitle =
      user?.firstName && isLoggedIn ? `Bonjour ${user.firstName}` : 'Bienvenue\u00a0!'

    const getSubtitle = () => {
      const shouldSeeDefaultSubtitle =
        !isLoggedIn ||
        !user ||
        !isCurrentOrFormerBeneficiary(user) ||
        user.eligibility === EligibilityType.free ||
        user?.creditType === UserCreditType.CREDIT_V3_FREE ||
        (!isCurrentBeneficiary(user) && user.isEligibleForBeneficiaryUpgrade)

      if (shouldSeeDefaultSubtitle) return 'Toute la culture à portée de main'

      const shouldSeeBeneficiarySubtitle =
        isCurrentBeneficiary(user) && !!availableCredit && !availableCredit.isExpired
      if (shouldSeeBeneficiarySubtitle) {
        const credit = formatCurrencyFromCents(
          availableCredit.amount,
          currency,
          euroToPacificFrancRate
        )
        return `Tu as ${credit} sur ton pass`
      }
      return 'Ton crédit est expiré'
    }
    if (isDesktopViewport) {
      return (
        <React.Fragment>
          <Spacer.TopScreen />
          <HeaderContainer>
            <TitleContainer>
              <TitleLabel numberOfLines={1}>{welcomeTitle}</TitleLabel>
              <Subtitle>{getSubtitle()}</Subtitle>
            </TitleContainer>
            <LocationWidgetDesktopContainer testID="web-location-widget">
              <StyledSeparator height={height} />
              <LocationWidgetDesktop />
            </LocationWidgetDesktopContainer>
          </HeaderContainer>
        </React.Fragment>
      )
    }

    return (
      <PageHeader
        title={welcomeTitle}
        subtitle={getSubtitle()}
        numberOfLines={numberOfLines}
        withMargins>
        {isDesktopViewport ? null : <LocationWidget screenOrigin={ScreenOrigin.HOME} />}
      </PageHeader>
    )
  }, [
    user,
    isLoggedIn,
    isDesktopViewport,
    numberOfLines,
    availableCredit,
    currency,
    euroToPacificFrancRate,
    height,
  ])

  return Header
}

const HeaderContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginTop: theme.designSystem.size.spacing.xl,
  marginHorizontal: theme.contentPage.marginHorizontal,
  zIndex: theme.zIndex.header,
}))

const TitleContainer = styled.View({
  flex: 1,
})

const Subtitle = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
  marginTop: theme.designSystem.size.spacing.xs,
}))

const TitleLabel = styled(Typo.Title1)({
  maxWidth: '100%',
})

const LocationWidgetDesktopContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  zIndex: theme.zIndex.locationWidget,
  marginLeft: theme.designSystem.size.spacing.s,
}))

const StyledSeparator = styled(Separator.Vertical)(({ theme }) => ({
  marginLeft: theme.designSystem.size.spacing.xl,
  marginRight: theme.designSystem.size.spacing.l,
}))
